import { HttpResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ChangeDetectorRef,
  DestroyRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrganizationCatalogInfoVM } from 'src/app/shared/services/rnapi2-service/models/OrganizationCatalogInfoVM';
import { CartService } from '../../../services/cart/cart.service';
import { LoggedInInfoService } from '../../../services/loggedInInfo/logged-in-info.service';
import { OrganizationService, UserService } from '../../../services/rnapi2-service/apis/api';
import {
  RnCommonId,
  RnOrganizationConfigurationVM,
  RnOrganizationConfigurationVMRNResponseRelay,
  RnOrganizationPaymentOnFileVM,
  RnOrganizationsProfileVM,
  RnOrganizationsProfileVMRNResponseRelay,
  RnOrganizationWLPContactInformationVM,
  RnOrganizationWLPContactInformationVMRNResponseRelay,
  RnUserPackagePoolAction,
  RnUsersProfileVM
} from '../../../services/rnapi2-service/models/models';
import { RnsidebarService } from '../../../services/sidebar/rnsidebar.service';
import { PackageCardData } from '../package-card/package-card-data';
import { SignalrPollingService } from 'src/app/shared/services/signalr-polling/signalr-polling.service';
import { SignalrPollingBaseMessage } from 'src/app/core/models/signalr/signalr-polling-base-message';
import { PackagesService } from 'src/app/shared/services/packages/packages.service';
import { PackageUpdateEventData } from 'src/app/shared/services/packages/package-update-event-data.model';
import { ToasterService, ToastType } from '@rn-platform/frontend-shared-data-access-toaster';
import { UserPackageChangeModalService } from './user-package-change-modal.service';
import { ModalState } from 'src/app/shared/services/base-modal/base-modal.service';
import { UserPackageChangeConfiguration } from './user-package-change-configuration';
import { ModalComponent } from '@rn-platform/frontend-shared-ui-common';
import { UserPackageChangeOptionsConfiguration } from '../user-package-change-options/user-package-change-options-configuration';
import { UserPackageChangeOptionsComponent } from '../user-package-change-options/user-package-change-options.component';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';

const UNPURCHASABLE = 'Unpurchasable';
const NO_PACKAGE_POOL_ID = -2;
const ACTION_REASSIGN = 'reassign';
const ACTION_DROP = 'drop';
const ACTION_ADD = 'add';
const ACTION_UPGRADE = 'upgrade';
const ACTION_DOWNGRADE = 'downgrade';

@Component({
  selector: 'app-user-package-change',
  templateUrl: './user-package-change.component.html',
  styleUrls: ['./user-package-change.component.scss'],
  standalone: false
})
export class UserPackageChangeComponent implements OnInit, OnDestroy {
  @ViewChild('cartIcon') cartIcon: ElementRef;
  @ViewChild('cart') cart: ElementRef;
  @ViewChild('modalRef') modalComponent: ModalComponent;
  @ViewChild('confirmationModalRef') confirmationModal: ConfirmationModalComponent;
  @ViewChild('userPackageChangeOptionsComponent') userPackageChangeOptionsComponent: UserPackageChangeOptionsComponent;

  private readonly formBuilder = inject(FormBuilder);
  private readonly organizationService = inject(OrganizationService);
  private readonly cartService = inject(CartService);
  private readonly renderer = inject(Renderer2);
  private readonly userService = inject(UserService);
  private readonly rnsidebarService = inject(RnsidebarService);
  private readonly toasterService = inject(ToasterService);
  private readonly loggedInInfoService = inject(LoggedInInfoService);
  private readonly signalrPollingService = inject(SignalrPollingService);
  private readonly packageService = inject(PackagesService);
  private readonly userPackageChangeModalService = inject(UserPackageChangeModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private packageswithPackagePools: PackageCardData[];
  private hasRightToPurchase: boolean;
  private selectedUser?: any;
  public totalPkgCount: number = 0;
  public keepOldPackage: boolean;
  public canAddDrop: boolean;
  public initialPackage: PackageCardData;
  public packageData: PackageCardData[] = [];
  public paymentInfo: RnOrganizationPaymentOnFileVM;
  public packagesForm: FormGroup;
  public orgProfile: RnOrganizationsProfileVM;
  public orgConfig: RnOrganizationConfigurationVM;
  public orgWlpContactInfo: RnOrganizationWLPContactInformationVM;
  public addOns: PackageCardData[] = [];
  public assignedPackage: string;
  public organizationId: number;
  public isShoppingCartVisible: boolean = false;
  public fullName: string;
  public userType: string;
  public loading: boolean = false;
  public loadingPackages: boolean = false;
  public checkForInitialActiveMessage = false;
  public selectedPackage: PackageCardData;
  public availableAddOns: PackageCardData[] = [];
  public userData: RnUsersProfileVM;
  public wlpPackageText: string = '';
  public partnerName: string;
  public showPkgSubtextSection = false;
  public cancellations_phone: string;
  public technical_support_phone: string;
  public userId: number;
  public showOldPackageOptionsStep: boolean = false;
  public userPackageChangeOptionsConfiguration: UserPackageChangeOptionsConfiguration;
  public hideModal: boolean = false;

  ngOnInit(): void {
    this.loading = true;
    this.initializePackageForm();
    this.addRendererClickListener();
    this.addSignalRPollingHandler();
    this.subscribeModalService();
  }

  ngOnDestroy(): void {
    this.cartService.clearCart();
  }

  public packageSelected(pkg: PackageCardData): void {
    this.selectedPackage = pkg;
  }

  public packagesLoaded(): void {
    this.getWlpPackageText();
    this.loadingPackages = false;
    this.cdr.detectChanges();
  }

  public componentLoading = (): boolean => this.loading || this.loadingPackages;

  public canSubmit(): boolean {
    if (this.loading) {
      return false;
    }
    if (this.showOldPackageOptionsStep) {
      return this.userPackageChangeOptionsComponent?.canSubmit();
    } else {
      return this.packageSelectionUpdated();
    }
  }

  public packageSelectionUpdated(): boolean {
    return this.selectedPackage?.PackagePoolID != this.initialPackage?.PackagePoolID
  }

  public open(): void {
    this.hideModal = false;
    this.modalComponent?.open();
  }

  public close(saved = false): void {
    if (this.packageSelectionUpdated() && !saved) {
      this.hide();
      this.confirmationModal?.open();
    } else {
      this.modalComponent?.close();
    }
  }

  public hide(): void {
    this.hideModal = true;
  }
  
  public show(): void {
    this.hideModal = false;
  }

  public confirmLeavePage(): void {
    this.confirmationModal?.close();
    this.modalComponent?.close();
  }

  public cancelLeavePage(): void {
    this.confirmationModal?.close();
    this.show();
  }

  public getWlpPackageText(): void {
    this.showPkgSubtextSection = false;
    if (this.orgProfile?.CustomerType != 'Rightworks' && this.orgProfile?.ParentID < 0) {
      this.partnerName = this.orgProfile.PartnerName;
      this.showPkgSubtextSection = true;
      if (!this.loggedInInfoService.GetWLPConfig) return;
      const wlpConfigData = this.loggedInInfoService?.GetWLPConfig()?.data;
      if (!wlpConfigData) return;
      this.wlpPackageText = Object.values(wlpConfigData)[Object.keys(wlpConfigData).indexOf('change_pkg_message')];
    }
  }

  public subscribeModalService(): void {
    this.userPackageChangeModalService.modalState$.subscribe((data: ModalState<UserPackageChangeConfiguration>) => {
      if (!data?.config?.userId) return;
      this.cartService.clearCart();
      this.loading = true;
      this.showOldPackageOptionsStep = false;
      this.userPackageChangeOptionsConfiguration = null;
      this.selectedPackage = null;
      this.assignedPackage = null;
      this.userId = data.config.userId;
      this.initializePackageForm();
      this.getSupportPhoneNumbers();
      this.loadUserPackageData(this.userId);
      if (data?.isOpen) {
        this.open();
      } else {
        this.close();
      }
    });
  }

  public loadUserPackageData(userId: number): void {
    this.loading = true;
    const loggedInUser = this.loggedInInfoService.GetLoggedInUser();
    const payloadCommonId = new RnCommonId();
    payloadCommonId.Id = userId.toString();
    this.userService
      .apiV2UsersGetuserPost(payloadCommonId)
      .pipe(
        switchMap((response) => {
          this.handleUserDataResponse(response.data);
          return forkJoin([
            this.loadOrganizationData(this.organizationId.toString()),
            this.loadOrganizationConfig(this.organizationId.toString()),
            this.loadOrganizationWLPConfig(this.organizationId.toString())
          ]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ([orgProfile, orgConfig, orgWlpContactInfo]) => {
          this.handleOrgData(orgProfile.body.data, orgConfig.body.data, orgWlpContactInfo.body.data, loggedInUser);
        },
        error: (err) => {
          console.error('Error loading organization data:', err);
        }
      });
  }

  public handleUserDataResponse(data: any): void {
    this.userData = this.selectedUser = data;
    this.assignedPackage = data.AssignedPackage;
    this.organizationId = data.OrganizationID;
    this.fullName = `${data.FirstName} ${data.LastName}`;
    this.userType = data.UserType;
  }

  public handleOrgData(
    orgProfile: RnOrganizationsProfileVM,
    orgConfig: RnOrganizationConfigurationVM,
    orgWlpContactInfo: RnOrganizationWLPContactInformationVM,
    loggedInUser: any
  ): void {
    this.orgProfile = orgProfile;
    this.orgConfig = orgConfig;
    this.orgWlpContactInfo = orgWlpContactInfo;
    this.hasRightToPurchase = this.computeHasRightToPurchase(loggedInUser);
    this.keepOldPackage = !this.hasRightToPurchase;
    this.loadPaymentInfo();
    this.loadAndHandlePackageData();
  }

  public computeHasRightToPurchase(loggedInUser: any): boolean {
    const orgIsInWriteOff = this.orgProfile.Status === 'WriteOff' || this.orgProfile.StatusDesc === 'Write-Off';
    const orgIsSuspended = this.orgProfile.Status === 'Suspended' || this.orgProfile.StatusDesc === 'Suspended';
    const orgIsInAddOnSuspended =
      this.orgProfile.Status === 'AddOnSuspended' || this.orgProfile.StatusDesc === 'Add-ons Suspended';
    return (
      this.loggedInInfoService.loggedInUserHasRights(['PCHDRPKUSR']) &&
      (loggedInUser.IsInternal() || this.organizationId === this.orgProfile.IdThatPaysForThisOrg) &&
      !orgIsInWriteOff &&
      !orgIsSuspended &&
      !orgIsInAddOnSuspended
    );
  }

  public loadPaymentInfo(): void {
    const loggedInUserOrgId = this.loggedInInfoService.GetLoggedInUserOrg().ID;
    const organizationIdPayload = new RnCommonId();
    organizationIdPayload.Id = loggedInUserOrgId.toString();
    this.organizationService
      .apiV2OrganizationsCurrentpaymentinfoPost(organizationIdPayload, 'response')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((r) => {
        if (r.body.Success) {
          this.paymentInfo = r.body.data;
        } else {
          console.error('Failed to load payment info for organization ID:', loggedInUserOrgId);
        }
      });
  }

  public loadAndHandlePackageData(): void {
    this.loadOrganizationPackageData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r: HttpResponse<OrganizationCatalogInfoVM>) => {
          if (r.body.Success) {
            this.handlePackageData(r.body.data.CatalogItems);
          }
        },
        error: (err) => {
          this.loading = false;
          console.error('Error loading organization package data:', err);
        },
        complete: () => {
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  public handlePackageData(catalogItems: any[]): void {
    this.packageData = [];
    const packages = catalogItems.filter((p) => p.PackageUIType === 'package');
    packages.forEach((p) => {
      const pkg = new PackageCardData();
      pkg.AssignFromRnCatalogItemVM(p);
      this.packageData.push(pkg);
    });

    this.addOns = this.packageData.filter((p) => p.PackageUIType === 'addon' && p.PackageTypeID !== 6);

    if (this.assignedPackage) {
      this.handleAssignedPackage();
    } else {
      this.initialPackage = new PackageCardData();
      this.initialPackage.PackagePoolID = NO_PACKAGE_POOL_ID;
    }

    this.packageswithPackagePools = this.packageData
      .filter(
        (pkg) =>
          !(pkg.CatalogVisibility == UNPURCHASABLE && pkg.CurrentQuantity == 0) &&
          (!pkg.CatalogCategoryName || pkg.CatalogCategoryName === 'OneSpaceDesktop')
      )
      .sort((x) => x.SortOrder);

    this.totalPkgCount = 0;
    this.packageswithPackagePools.forEach((pkg) => {
      this.totalPkgCount += pkg.CurrentQuantity;
    });

    this.canAddDrop = this.packageswithPackagePools.some((p) => p.CatalogVisibility != UNPURCHASABLE);
  }

  public handleAssignedPackage(): void {
    const selectedpackageList = this.packageData.filter((x) => x.PackageName === this.assignedPackage);
    if (selectedpackageList.length > 0) {
      const pkg = selectedpackageList[0];
      pkg.Selected = true;
      this.initialPackage = pkg;
      this.addOns.forEach((a) => {
        if (a.AddOnAdded) {
          a.AddOnAdded = false;
          if (a.BuyNew) {
            this.cartService.removeFromCart(a);
            a.BuyNew = false;
            a.CurrentQuantity--;
          }
          a.CurrentUsed--;
        }
      });
    }
  }

  public getSupportPhoneNumbers(): void {
    if (this.loggedInInfoService.GetWLPConfig) {
      const wlpConfigData = this.loggedInInfoService?.GetWLPConfig()?.data;
      if (wlpConfigData) {
        this.cancellations_phone =
          Object.values(wlpConfigData)[Object.keys(wlpConfigData).indexOf('cancellations_phone_number')];
        this.technical_support_phone =
          Object.values(wlpConfigData)[Object.keys(wlpConfigData).indexOf('technical_support_phone_number')];
      }
    }
  }

  public initializePackageForm(): void {
    this.packagesForm = this.formBuilder.group({
      selectedPackage: ['', Validators.required.bind(Validators)]
    });
  }

  public addRendererClickListener() {
    this.renderer.listen('window', 'click', (e: Event) => {
      if (!this.cartIcon.nativeElement.contains(e.target) && !this.cart.nativeElement.contains(e.target)) {
        this.closeShoppingCart();
      }
    });
  }

  public addSignalRPollingHandler() {
    this.signalrPollingService.AddMessageHandler((message) => {
      const notification = JSON.parse(message) as SignalrPollingBaseMessage;
      if (
        notification?.Payload?.AreaDataType?.toUpperCase() === 'APPLICATION_INSTALL_PAYLOAD' &&
        notification?.Payload?.Area?.toUpperCase() === 'USER' &&
        notification?.Payload?.AreaID === this.loggedInInfoService.GetLoggedInUser()?.UserID
      ) {
        const data = JSON.parse(notification.Payload.AreaData);
        if (data.Status === 'Removing' || data.Status === 'Installing') {
          setTimeout(() => {
            this.rnsidebarService.startLoading();
            this.rnsidebarService.refreshUser();
          }, 2000);
        }
      }
    });
  }

  public closeShoppingCart = () => (this.isShoppingCartVisible = false);

  public toggleShoppingCart = () => (this.isShoppingCartVisible = !this.isShoppingCartVisible);

  public onKeepOldPackageOptionSelected(keepOldPackageSelected: boolean) {
    this.keepOldPackage = keepOldPackageSelected;
  }

  public lastPackage(): boolean {
    return (
      this.totalPkgCount < 2 &&
      this.selectedPackage.PackagePoolID == NO_PACKAGE_POOL_ID &&
      !(this.initialPackage?.CatalogVisibility !== UNPURCHASABLE && this.canAddDrop ? this.keepOldPackage : true)
    );
  }
  
  // Save the changes will either save directly from the package change step or from the package options step
  // if the user needs to choose what to do with their old package: release or drop.  The first step
  // will determine if we need to show the package options step or not.
  public saveChanges(): void {
    if (this.showOldPackageOptionsStep) {
      this.saveChangeFromOldPackageOptionsStep();
    } else {
      this.saveChangesDirectlyFromChangeStep();
    }
  }

  private saveChangesDirectlyFromChangeStep(): void {
    const catVis = this.packageswithPackagePools.filter((p) => p.PackageName === this.selectedUser.AssignedPackage);
    if (this.initialPackage.PackageID > 0 && catVis.length > 0 && catVis[0].CatalogVisibility !== UNPURCHASABLE) {
      this.selectedUser.DirectBilledPackages = [this.initialPackage.PackageName];
      this.selectedUser.FullName = `${this.selectedUser.FirstName} ${this.selectedUser.LastName}`;
      this.userPackageChangeOptionsConfiguration = new UserPackageChangeOptionsConfiguration(
        [this.selectedUser],
        catVis[0].CatalogVisibility,
        false
      );
      this.showOldPackageOptionsStep = true;
    } else {
      this.save();
    }
  }

  public saveChangeFromOldPackageOptionsStep(): void {
    if (this.lastPackage()) {
      const message = `Need to drop your final package and cancel your Rightworks account? Please contact us for ${this.cancellations_phone}.`;
      const id = 'apphub-manage-packages-sidebar-last-package-error-toast';
      this.toasterService.addToast(ToastType.Error_, message, message, id, id);
    } else {
      this.save();
    }
  }

  public save(): void {
    this.loading = true;
    const promoID = this.cartService.promo?.promoCode?.ID ?? -1;
    const keepOldPackage =
      this.initialPackage?.CatalogVisibility !== UNPURCHASABLE && this.canAddDrop ? this.keepOldPackage : true;
    const atomicAction = this.getAtomicAction(this.selectedPackage, this.initialPackage, keepOldPackage);
    const packageActionPayload: RnUserPackagePoolAction = {
      AffectedOrganizationId: this.orgProfile.IdThatPaysForThisOrg,
      Users_ID: this.userId,
      ToPackage_ID: this.selectedPackage.PackageID,
      AtomicAction: atomicAction,
      KeepOldPackage: keepOldPackage,
      PromotionCodeID: promoID
    };

    // Leaving temporary log for debugging purposes, remove with 
    console.log('Action for license selection:', atomicAction);

    this.userService
      .apiV2UsersPackagepoolactionPost(packageActionPayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.handlePackageUpdateResponse(response);
          this.close(true);
        },
        error: (error) => {
          console.log(error);
          this.loading = false;
          const message = `An error occured when changing packages. Please contact ${this.technical_support_phone}.`;
          const id = 'apphub-manage-package-change-error-toast';
          this.toasterService.addToast(ToastType.Error_, message, message, id, id);
        }
      });
  }

  public handlePackageUpdateResponse(response: any): void {
    if (response) {
      setTimeout(() => {
        this.loading = false;
        const message = 'Package changed successfully';
        const id = 'apphub-manage-package-changed-success-toast';
        this.toasterService.addToast(ToastType.Success_, message, message, id, id);
        this.rnsidebarService.refreshUser();
        this.packageService.updatePackageEvent(new PackageUpdateEventData(this.userId, this.selectedPackage));
      }, 3000);
      const currentUser = this.loggedInInfoService.GetLoggedInUser();
      if (currentUser.UserID == this.userId) {
        currentUser.AssignedPackage = this.selectedPackage.PackageName;
        this.loggedInInfoService.SetLoggedInUser(currentUser);
      }
    }
  }

  public getAtomicAction(toPackage: PackageCardData, fromPackage: PackageCardData, keepOld: boolean): string {
    const ownsToPackage = !toPackage.BuyNew;
    if (toPackage.PackagePoolID == NO_PACKAGE_POOL_ID) {
      return keepOld ? ACTION_REASSIGN : ACTION_DROP;
    } else if (fromPackage.PackagePoolID == NO_PACKAGE_POOL_ID) {
      return ownsToPackage ? ACTION_REASSIGN : ACTION_ADD;
    }
    const toHigherPrice = toPackage.ListPrice > fromPackage.ListPrice;
    if (toHigherPrice) {
      if (ownsToPackage) {
        return keepOld ? ACTION_REASSIGN : ACTION_DROP;
      } else {
        return keepOld ? ACTION_ADD : ACTION_UPGRADE;
      }
    } else {
      if (ownsToPackage) {
        return keepOld ? ACTION_REASSIGN : ACTION_DROP;
      } else {
        return keepOld ? ACTION_ADD : ACTION_DOWNGRADE;
      }
    }
  }

  public loadOrganizationData(orgId: string): Observable<HttpResponse<RnOrganizationsProfileVMRNResponseRelay>> {
    const payload = new RnCommonId();
    payload.Id = orgId;
    return this.organizationService.apiV2OrganizationsProfilePost(payload, 'response');
  }

  public loadOrganizationConfig(
    orgId: string
  ): Observable<HttpResponse<RnOrganizationConfigurationVMRNResponseRelay>> {
    const payload = new RnCommonId();
    payload.Id = orgId;
    return this.organizationService.apiV2OrganizationsGetorganizationconfigurationPost(payload, 'response');
  }

  public loadOrganizationWLPConfig(
    orgId: string
  ): Observable<HttpResponse<RnOrganizationWLPContactInformationVMRNResponseRelay>> {
    const payload = new RnCommonId();
    payload.Id = orgId;
    return this.organizationService.apiV2OrganizationsGetorganizationwlpcontactinfoPost(payload, 'response');
  }

  public loadOrganizationPackageData(): Observable<HttpResponse<OrganizationCatalogInfoVM>> {
    const payload = new RnCommonId();
    payload.Id = this.organizationId.toString();
    payload.AffectedOrganizationId = this.organizationId;
    return this.organizationService.apiV2OrganizationsGetcatalogitemsfororgPost(payload, 'response');
  }
}
